import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import SpecialCollectionService from '../services/SpecialCollectionService';
import StoreService from '../components/StoreService';
import ProductService from '../components/ProductService';
import { useGetBalance } from '../features/inventory/api/queries';
import '../css/special-collections.css';
import '../css/special-collections-enhancements.css';

const ProductsModal = ({ show, onClose, products, storeName, allProducts }) => {
  if (!show) return null;

  // Helper to normalize MongoDB ObjectId
  const normalizeId = (id) => {
    if (!id) return null;
    if (typeof id === 'string') return id;
    if (id.$oid) return id.$oid;
    if (id._id) return id._id;
    return String(id);
  };

  const getProductImage = (productId) => {
    const normalizedProductId = normalizeId(productId);
    const product = allProducts.find(p => normalizeId(p._id) === normalizedProductId);
    if (!product || !product.url_product) return null;
    
    const normalized = `${product.url_product}`.replace(/^\//, '');
    if (normalized.startsWith('http')) {
      return normalized;
    }
    return `${process.env.REACT_APP_DATA_PIC_PATH || 'https://office.le-celadon.ma'}/${normalized}`;
  };

  const getProductDetails = (productId) => {
    const normalizedProductId = normalizeId(productId);
    return allProducts.find(p => normalizeId(p._id) === normalizedProductId);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Tous les produits - {storeName}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="products-grid">
            {products.map((product, idx) => {
              const productDetails = getProductDetails(product.product_id);
              return (
                <div key={idx} className={`product-card-modal ${product.quantity > 0 ? 'has-quantity-warning' : ''}`}>
                  <div className="product-image">
                    {getProductImage(product.product_id) ? (
                      <img src={getProductImage(product.product_id)} alt={productDetails?.name || 'Product'} />
                    ) : (
                      <div className="product-no-image">No Image</div>
                    )}
                  </div>
                  <div className="product-info">
                    <h5>{productDetails?.name || 'Unknown Product'}</h5>
                    <p>Quantité: {product.quantity}</p>
                    {product.quantity > 0 && <p>Quantité réelle: {product.quantity_real || 0}</p>}
                    <span className={`status-badge status-${product.status}`}>
                      {product.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const ManageProductsModal = ({ show, onClose, collectionId, storeId, storeName, allProducts, existingProducts = [], onSave }) => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 20;

  // Helper to normalize MongoDB ObjectId
  const normalizeId = (id) => {
    if (!id) return null;
    if (typeof id === 'string') return id;
    if (id.$oid) return id.$oid;
    if (id._id) return id._id;
    return String(id);
  };

  useEffect(() => {
    if (show && existingProducts.length > 0) {
      setSelectedProducts(existingProducts.map(p => ({
        product_id: p.product_id,
        quantity: p.quantity || 0,
        quantity_real: p.quantity_real || 0,
        status: p.status || 'pending'
      })));
    } else if (show) {
      setSelectedProducts([]);
    }
    // Reset to page 1 when modal opens
    if (show) {
      setCurrentPage(1);
    }
  }, [show, existingProducts]);

  if (!show) return null;

  const getProductImage = (product) => {
    if (!product?.url_product) return null;
    
    const normalized = `${product.url_product}`.replace(/^\//, '');
    if (normalized.startsWith('http')) {
      return normalized;
    }
    return `${process.env.REACT_APP_DATA_PIC_PATH || 'https://office.le-celadon.ma'}/${normalized}`;
  };

  const filteredProducts = allProducts.filter(product =>
    product.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Reset to page 1 when search changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleQuantityChange = (productId, field, value) => {
    setSelectedProducts(prev => {
      const existing = prev.find(p => p.product_id === productId);
      if (existing) {
        return prev.map(p =>
          p.product_id === productId ? { ...p, [field]: parseFloat(value) || 0 } : p
        );
      } else {
        return [...prev, { product_id: productId, quantity: 0, quantity_real: 0, [field]: parseFloat(value) || 0 }];
      }
    });
  };

  const getProductQuantity = (productId, field) => {
    const product = selectedProducts.find(p => p.product_id === productId);
    return product?.[field] || 0;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const productsToSave = selectedProducts.filter(p => p.quantity > 0).map(p => ({
        product_id: normalizeId(p.product_id),
        quantity: p.quantity,
        quantity_real: p.quantity_real || 0,
        status: 'pending'
      }));

      await SpecialCollectionService.updateStoreProducts(
        normalizeId(collectionId), 
        normalizeId(storeId), 
        productsToSave
      );
      
      alert('Produits mis à jour avec succès');
      setSelectedProducts([]);
      // Pass the updated products back to parent
      onSave(collectionId, storeId, productsToSave);
      onClose();
    } catch (error) {
      console.error('Error saving products:', error);
      alert('Erreur lors de la mise à jour des produits');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Gérer les produits - {storeName}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          
          {filteredProducts.length > 0 && (
            <div className="pagination-info">
              Affichage {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} sur {filteredProducts.length} produits
            </div>
          )}

          <div className="products-manage-grid">
            {currentProducts.map((product) => (
              <div key={product._id} className={`product-manage-card ${getProductQuantity(product._id, 'quantity') > 0 ? 'has-quantity-warning' : ''}`}>
                <div className="product-image-small">
                  {getProductImage(product) ? (
                    <img 
                      src={getProductImage(product)} 
                      alt={product.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="product-no-image-small"><i class="fa-solid fa-image"></i></div>';
                      }}
                    />
                  ) : (
                    <div className="product-no-image-small">
                      <i className="fa-solid fa-image"></i>
                    </div>
                  )}
                </div>
                <div className="product-manage-info">
                  <h6 title={product.name}>{product.name}</h6>
                  <div className="quantity-inputs">
                    <div className="input-group-small">
                      <label>Qté</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={getProductQuantity(product._id, 'quantity')}
                        onChange={(e) => handleQuantityChange(product._id, 'quantity', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    {getProductQuantity(product._id, 'quantity') > 0 && (
                      <div className="input-group-small">
                        <label>Qté réelle</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={getProductQuantity(product._id, 'quantity_real')}
                          onChange={(e) => handleQuantityChange(product._id, 'quantity_real', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                <i className="fa-solid fa-angles-left"></i>
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="fa-solid fa-chevron-left"></i>
              </button>
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                  // Show first page, last page, current page, and pages around current
                  const showPage = pageNum === 1 || 
                                   pageNum === totalPages || 
                                   Math.abs(pageNum - currentPage) <= 1;
                  
                  const showEllipsisBefore = pageNum === currentPage - 2 && currentPage > 3;
                  const showEllipsisAfter = pageNum === currentPage + 2 && currentPage < totalPages - 2;
                  
                  if (showEllipsisBefore || showEllipsisAfter) {
                    return <span key={pageNum} className="pagination-ellipsis">...</span>;
                  }
                  
                  if (!showPage) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <i className="fa-solid fa-chevron-right"></i>
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                <i className="fa-solid fa-angles-right"></i>
              </button>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Annuler
          </button>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SpecialCollections = () => {
  const [collections, setCollections] = useState([]);
  const [stores, setStores] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedStoreFilter, setSelectedStoreFilter] = useState(null);
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' | 'filtered' | 'general'
  const [loading, setLoading] = useState(true);
  const [inventoryBalances, setInventoryBalances] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedStoreProducts, setSelectedStoreProducts] = useState({ products: [], storeName: '' });
  const [selectedStoresForNew, setSelectedStoresForNew] = useState([]);
  const [showManageProductsModal, setShowManageProductsModal] = useState(false);
  const [currentStoreForProducts, setCurrentStoreForProducts] = useState({ 
    collectionId: null, 
    storeId: null, 
    storeName: '', 
    existingProducts: []
  });
  const [showAddStoresModal, setShowAddStoresModal] = useState(false);
  const [selectedCollectionForStores, setSelectedCollectionForStores] = useState(null);
  const [selectedStoresToAdd, setSelectedStoresToAdd] = useState([]);
  const [showQuickEditModal, setShowQuickEditModal] = useState(false);
  const [quickEditProduct, setQuickEditProduct] = useState(null);
  const [generalSearchTerm, setGeneralSearchTerm] = useState('');
  const [showAddStoreToProductModal, setShowAddStoreToProductModal] = useState(false);
  const [addStoreToProduct, setAddStoreToProduct] = useState(null);
  const [showEditQuantityModal, setShowEditQuantityModal] = useState(false);
  const [editQuantityData, setEditQuantityData] = useState(null);
  const [generalStoreFilter, setGeneralStoreFilter] = useState([]);
  
  // Form states
  const [newCollection, setNewCollection] = useState({
    name: '',
    description: '',
    status: 'draft'
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [collectionsData, storesData, productsData] = await Promise.all([
        SpecialCollectionService.getCollections(),
        StoreService.fetchStores(),
        ProductService.fetchProducts()
      ]);

      setCollections(collectionsData.collections || []);
      const fetchedStores = storesData.response || storesData || [];
      setStores(fetchedStores);
      setAllProducts(productsData.response || productsData || []);

      // Fetch inventory balances for all stores
      await loadInventoryBalances(fetchedStores);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadInventoryBalances = async (storesList) => {
    try {
      const { apiClient } = await import('../features/inventory/api/client');
      const balancesData = {};
      
      // Fetch inventory balances for each store
      await Promise.all(storesList.map(async (store) => {
        try {
          const storeId = normalizeId(store._id);
          const { data } = await apiClient.get(`/inventory/store/${storeId}/products`);
          
          // Create a map of product_id -> available quantity
          const productBalanceMap = {};
          if (Array.isArray(data)) {
            data.forEach(balance => {
              const productId = normalizeId(balance.product_id);
              if (balance.stock_state === 'available') {
                productBalanceMap[productId] = (productBalanceMap[productId] || 0) + (balance.qty || balance.quantity || 0);
              }
            });
          }
          
          balancesData[storeId] = productBalanceMap;
        } catch (err) {
          console.warn(`Failed to load inventory for store ${store.name}:`, err);
          balancesData[normalizeId(store._id)] = {};
        }
      }));
      
      setInventoryBalances(balancesData);
    } catch (error) {
      console.error('Error loading inventory balances:', error);
      setInventoryBalances({});
    }
  };

  // Quick reload - only reloads collections and inventory, not products
  const reloadCollections = async () => {
    try {
      const collectionsData = await SpecialCollectionService.getCollections();
      setCollections(collectionsData.collections || []);
      
      // Reload inventory balances with existing stores
      if (stores.length > 0) {
        await loadInventoryBalances(stores);
      }
    } catch (error) {
      console.error('Error reloading collections:', error);
      alert('Erreur lors du rechargement des collections');
    }
  };

  const handleCreateCollection = async (e) => {
    e.preventDefault();
    try {
      const collectionData = {
        ...newCollection,
        stores: selectedStoresForNew.map(store => ({
          store_id: store.value,
          products: []
        }))
      };
      const result = await SpecialCollectionService.createCollection(collectionData);
      
      // Optimistic update - add to state immediately
      const newCollectionWithId = {
        ...collectionData,
        _id: result._id || result.collection?._id || Date.now().toString(),
        created_at: new Date().toISOString()
      };
      setCollections(prev => [newCollectionWithId, ...prev]);
      
      setShowAddModal(false);
      setNewCollection({ name: '', description: '', status: 'draft' });
      setSelectedStoresForNew([]);
      alert('Collection créée avec succès');
    } catch (error) {
      console.error('Error creating collection:', error);
      alert('Erreur lors de la création de la collection');
      loadInitialData(); // Reload only on error
    }
  };

  const handleDeleteCollection = async (collectionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette collection ?')) {
      return;
    }
    try {
      // Optimistic update - remove from state immediately
      setCollections(prev => prev.filter(c => c._id !== collectionId));
      
      await SpecialCollectionService.deleteCollection(collectionId);
      alert('Collection supprimée avec succès');
    } catch (error) {
      console.error('Error deleting collection:', error);
      alert('Erreur lors de la suppression de la collection');
      loadInitialData(); // Reload only on error to restore state
    }
  };

  const handleOpenManageProducts = (collectionId, storeId, storeName, existingProducts = []) => {
    setCurrentStoreForProducts({ 
      collectionId, 
      storeId, 
      storeName, 
      existingProducts
    });
    setShowManageProductsModal(true);
  };

  const handleCloseManageProducts = () => {
    setShowManageProductsModal(false);
    setCurrentStoreForProducts({ 
      collectionId: null, 
      storeId: null, 
      storeName: '', 
      existingProducts: []
    });
  };

  const handleProductsSaved = async (collectionId, storeId, updatedProducts) => {
    // Reload collections (quick - without products) to ensure accurate render
    await reloadCollections();
  };

  const handleOpenQuickEdit = (collectionId, storeId, storeName, product) => {
    setQuickEditProduct({
      collectionId,
      storeId,
      storeName,
      product_id: product.product_id,
      quantity: product.quantity,
      quantity_real: product.quantity_real || 0,
      status: product.status
    });
    setShowQuickEditModal(true);
  };

  const handleQuickEditSave = async () => {
    if (!quickEditProduct) return;
    
    try {
      const { collectionId, storeId, product_id, quantity, quantity_real, status } = quickEditProduct;
      
      // Use specific product update endpoint - normalize IDs
      await SpecialCollectionService.updateProduct(
        normalizeId(collectionId), 
        normalizeId(storeId), 
        normalizeId(product_id), 
        {
          quantity,
          quantity_real,
          status
        }
      );
      
      setShowQuickEditModal(false);
      setQuickEditProduct(null);
      
      // Reload collections (quick - without products) to ensure accurate render
      await reloadCollections();
      
      alert('Produit mis à jour avec succès');
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Erreur lors de la mise à jour du produit');
      await reloadCollections();
    }
  };

  const handleDeleteProduct = async (collectionId, storeId, productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer ce produit ?')) {
      return;
    }
    
    try {
      // Use update endpoint to set status to "deleted" (soft delete)
      await SpecialCollectionService.updateProduct(
        normalizeId(collectionId), 
        normalizeId(storeId), 
        normalizeId(productId), 
        {
          status: 'deleted'
        }
      );
      
      // Reload collections (quick - without products) to ensure accurate render
      await reloadCollections();
      
      alert('Produit retiré avec succès');
    } catch (error) {
      console.error('Error removing product:', error);
      alert('Erreur lors du retrait du produit');
      await reloadCollections();
    }
  };

  const handleOpenAddStoreToProduct = (collection, productId, existingStores) => {
    setAddStoreToProduct({
      collectionId: collection._id,
      productId,
      existingStores: existingStores.map(id => normalizeId(id)),
      collectionStores: collection.stores || [],
      selectedStore: null,
      quantity: 0,
      quantityReal: 0
    });
    setShowAddStoreToProductModal(true);
  };

  const handleSaveAddStoreToProduct = async () => {
    if (!addStoreToProduct || !addStoreToProduct.selectedStore) {
      alert('Veuillez sélectionner un magasin');
      return;
    }

    try {
      const { collectionId, productId, selectedStore, quantity, quantityReal } = addStoreToProduct;
      const storeId = selectedStore.value;

      // Get collection and store
      const collection = collections.find(c => c._id === collectionId);
      const store = collection?.stores.find(s => normalizeId(s.store_id) === normalizeId(storeId));

      if (!store) {
        alert('Magasin non trouvé dans la collection');
        return;
      }

      // Add product to store's products
      const updatedProducts = [
        ...(store.products || [])
          .filter(p => normalizeId(p.product_id) !== normalizeId(productId))
          .map(p => ({
            product_id: normalizeId(p.product_id),
            quantity: p.quantity || 0,
            quantity_real: p.quantity_real || 0,
            status: p.status || 'pending'
          })),
        {
          product_id: normalizeId(productId),
          quantity: parseFloat(quantity) || 0,
          quantity_real: parseFloat(quantityReal) || 0,
          status: 'pending'
        }
      ];

      await SpecialCollectionService.updateStoreProducts(
        normalizeId(collectionId),
        normalizeId(storeId),
        updatedProducts
      );

      setShowAddStoreToProductModal(false);
      setAddStoreToProduct(null);
      await reloadCollections();
      alert('Magasin ajouté au produit avec succès');
    } catch (error) {
      console.error('Error adding store to product:', error);
      alert('Erreur lors de l\'ajout du magasin au produit');
    }
  };

  const handleOpenEditQuantity = (collectionId, storeId, storeName, productId, quantity, quantityReal) => {
    setEditQuantityData({
      collectionId,
      storeId,
      storeName,
      productId,
      quantity,
      quantityReal
    });
    setShowEditQuantityModal(true);
  };

  const handleSaveEditQuantity = async () => {
    if (!editQuantityData) return;

    try {
      const { collectionId, storeId, productId, quantity, quantityReal } = editQuantityData;

      await SpecialCollectionService.updateProduct(
        normalizeId(collectionId),
        normalizeId(storeId),
        normalizeId(productId),
        {
          quantity: parseFloat(quantity) || 0,
          quantity_real: parseFloat(quantityReal) || 0
        }
      );

      setShowEditQuantityModal(false);
      setEditQuantityData(null);
      await reloadCollections();
      alert('Quantité mise à jour avec succès');
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert('Erreur lors de la mise à jour de la quantité');
    }
  };

  const handleOpenAddStores = (collection) => {
    setSelectedCollectionForStores(collection);
    setSelectedStoresToAdd([]);
    setShowAddStoresModal(true);
  };

  const handleAddStoresToCollection = async () => {
    if (!selectedCollectionForStores || selectedStoresToAdd.length === 0) {
      alert('Veuillez sélectionner au moins un magasin');
      return;
    }
    
    try {
      const newStores = selectedStoresToAdd.map(store => ({
        store_id: store.value,
        products: []
      }));
      
      const updatedStores = [...(selectedCollectionForStores.stores || []), ...newStores];
      
      // Optimistic update - update state immediately
      setCollections(prev => prev.map(c => 
        c._id === selectedCollectionForStores._id 
          ? { ...c, stores: updatedStores }
          : c
      ));
      
      setShowAddStoresModal(false);
      setSelectedStoresToAdd([]);
      setSelectedCollectionForStores(null);
      
      await SpecialCollectionService.updateCollection(selectedCollectionForStores._id, {
        stores: updatedStores
      });
      
      alert('Magasins ajoutés avec succès');
    } catch (error) {
      console.error('Error adding stores:', error);
      alert('Erreur lors de l\'ajout des magasins');
      loadInitialData(); // Reload only on error
    }
  };

  const handleDeleteStoreFromCollection = async (collectionId, storeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer ce magasin de la collection ?')) {
      return;
    }
    
    try {
      const collection = collections.find(c => c._id === collectionId);
      if (!collection) return;
      
      const updatedStores = collection.stores.filter(
        store => normalizeId(store.store_id) !== normalizeId(storeId)
      );
      
      // Optimistic update - update state immediately
      setCollections(prev => prev.map(c => 
        c._id === collectionId 
          ? { ...c, stores: updatedStores }
          : c
      ));
      
      await SpecialCollectionService.updateCollection(collectionId, {
        stores: updatedStores
      });
      
      alert('Magasin retiré avec succès');
    } catch (error) {
      console.error('Error removing store:', error);
      alert('Erreur lors du retrait du magasin');
      loadInitialData(); // Reload only on error
    }
  };

  const getProductImage = (productId) => {
    const normalizedProductId = normalizeId(productId);
    const product = allProducts.find(p => normalizeId(p._id) === normalizedProductId);
    if (!product || !product.url_product) return null;
    
    const normalized = `${product.url_product}`.replace(/^\//, '');
    if (normalized.startsWith('http')) {
      return normalized;
    }
    return `${process.env.REACT_APP_DATA_PIC_PATH || 'https://office.le-celadon.ma'}/${normalized}`;
  };

  // Helper to normalize MongoDB ObjectId (handles both string and {$oid: string} formats)
  const normalizeId = (id) => {
    if (!id) return null;
    if (typeof id === 'string') return id;
    if (id.$oid) return id.$oid;
    if (id._id) return id._id;
    return String(id);
  };

  const getStoreName = (storeId) => {
    const normalizedStoreId = normalizeId(storeId);
    const store = stores.find(s => normalizeId(s._id) === normalizedStoreId);
    return store?.name || 'Unknown Store';
  };

  const getStoreById = (storeId) => {
    const normalizedStoreId = normalizeId(storeId);
    return stores.find(s => normalizeId(s._id) === normalizedStoreId);
  };

  const getProductDetails = (productId) => {
    const normalizedProductId = normalizeId(productId);
    return allProducts.find(p => normalizeId(p._id) === normalizedProductId);
  };

  // Helper to get count of stores that have this product in the collection
  const getProductStoreCount = (collectionId, productId) => {
    const collection = collections.find(c => c._id === collectionId);
    if (!collection) return 0;
    
    const normalizedProductId = normalizeId(productId);
    let count = 0;
    
      collection.stores?.forEach(store => {
      const hasProduct = store.products?.some(p => 
        normalizeId(p.product_id) === normalizedProductId && (p.status || '').toString().toLowerCase().trim() !== 'deleted'
      );
      if (hasProduct) count++;
    });
    
    return count;
  };

  // Helper to get total quantity_real across all stores for a product in the collection
  const getTotalQuantityReal = (collectionId, productId) => {
    const collection = collections.find(c => c._id === collectionId);
    if (!collection) return 0;
    
    const normalizedProductId = normalizeId(productId);
    let total = 0;
    
    collection.stores?.forEach(store => {
      const product = store.products?.find(p => 
        normalizeId(p.product_id) === normalizedProductId && (p.status || '').toString().toLowerCase().trim() !== 'deleted'
      );
      if (product) {
        total += (product.quantity_real || 0);
      }
    });
    
    return total;
  };

  const getCollectionProductSummary = (collection) => {
    if (!collection?.stores?.length) return [];

    const summaryMap = new Map();

    collection.stores.forEach(store => {
      const storeId = normalizeId(store.store_id);
      const storeName = getStoreName(store.store_id);
      const activeProducts = store.products?.filter(p => (p.status || '').toString().toLowerCase().trim() !== 'deleted' && getProductStoreCount(collection._id, p.product_id) > 0) || [];

      activeProducts.forEach(product => {
        const normalizedProductId = normalizeId(product.product_id);
        if (!summaryMap.has(normalizedProductId)) {
          summaryMap.set(normalizedProductId, {
            productId: normalizedProductId,
            productDetails: getProductDetails(product.product_id),
            sumQuantity: 0,
            sumQuantityReal: 0,
            storeEntries: []
          });
        }

        const entry = summaryMap.get(normalizedProductId);
        const quantity = product.quantity || 0;
        const quantityReal = product.quantity_real || 0;
        
        // Get available stock from inventory
        const availableStock = inventoryBalances[storeId]?.[normalizedProductId] || 0;

        entry.sumQuantity += quantity;
        entry.sumQuantityReal += quantityReal;
        entry.storeEntries.push({
          storeId,
          storeName,
          quantity,
          quantityReal,
          availableStock  // Add available stock from inventory
        });
      });
    });

    return Array.from(summaryMap.values()).sort((a, b) => {
      const nameA = (a.productDetails?.name || '').toLowerCase();
      const nameB = (b.productDetails?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  const printCollectionTable = (collection) => {
    try {
      const productsForPrint = getCollectionProductSummary(collection);
      const cols = ['Image', 'Produit', 'Disponible Total', 'Magasin', 'Quantité', 'Quantité Réelle', 'Statut'];
      const css = `
        body{font-family:Arial,Helvetica,sans-serif;padding:20px}
        table{border-collapse:collapse;width:100%}
        th,td{border:1px solid #ddd;padding:8px}
        th{background:#f4f4f4}
        img{max-width:60px;height:auto}
      `;
      let rowsHtml = '';
      productsForPrint.forEach(p => {
        const img = getProductImage(p.productId) || '';
        const totalAvailable = p.storeEntries.reduce((s,e) => s + (e.availableStock || 0), 0);
        rowsHtml += `<tr><td>${img?`<img src="${img}"/>`:''}</td><td>${(p.productDetails?.name||'')}</td><td>${totalAvailable}</td><td colspan='4'></td></tr>`;
        p.storeEntries.forEach(se => {
          rowsHtml += `<tr><td colspan='3'></td><td>${se.storeName || se.storeId}</td><td>${se.quantity || 0}</td><td>${se.quantityReal || 0}</td><td>${se.status || ''}</td></tr>`;
        });
      });

      const html = `<!doctype html><html><head><meta charset="utf-8"><title>Tableau produits - ${collection.name}</title><style>${css}</style></head><body><h2>Collection: ${collection.name}</h2><table><thead><tr>${cols.map(c=>`<th>${c}</th>`).join('')}</tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`;
      const w = window.open('', '_blank');
      if (!w) { alert('Impossible d\'ouvrir la fenêtre d\'impression (popup bloquée).'); return; }
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 250);
    } catch (err) {
      console.error('Print error', err);
      alert('Erreur lors de la génération du tableau');
    }
  };

  const showMoreProducts = (products, storeName) => {
    setSelectedStoreProducts({ products, storeName });
    setShowProductsModal(true);
  };

  const storeOptions = stores.map(store => ({
    value: store._id,
    label: store.name
  }));

  const filteredCollections = selectedStoreFilter
    ? collections.filter(collection =>
        collection.stores?.some(store => 
          normalizeId(store.store_id) === normalizeId(selectedStoreFilter.value)
        )
      )
    : collections;

  if (loading) {
    return <div className="loading-container">Chargement...</div>;
  }

  return (
    <div className="special-collections-container">
      <div className="page-header">
        <h1>Collections Spéciales</h1>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          <i className="fa-solid fa-plus"></i> Nouvelle Collection
        </button>
      </div>

      <div className="toolbar">
        <div className="view-mode-toggle">
          <button
            className={viewMode === 'grouped' ? 'active' : ''}
            onClick={() => setViewMode('grouped')}
          >
            <i className="fa-solid fa-table-cells"></i> Vue Groupée
          </button>
          <button
            className={viewMode === 'filtered' ? 'active' : ''}
            onClick={() => setViewMode('filtered')}
          >
            <i className="fa-solid fa-filter"></i> Filtrer par Magasin
          </button>
          <button
            className={viewMode === 'general' ? 'active' : ''}
            onClick={() => setViewMode('general')}
          >
            <i className="fa-solid fa-layer-group"></i> General Produit
          </button>
        </div>

        {viewMode === 'filtered' && (
          <div className="store-filter">
            <Select
              options={storeOptions}
              value={selectedStoreFilter}
              onChange={setSelectedStoreFilter}
              placeholder="Sélectionner un magasin..."
              isClearable
            />
          </div>
        )}

        {viewMode === 'general' && (
          <div className="store-filter">
            <Select
              isMulti
              options={storeOptions}
              value={generalStoreFilter}
              onChange={setGeneralStoreFilter}
              placeholder="Filtrer par magasins..."
              isClearable
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        )}
      </div>

      <div className="collections-list">
        {filteredCollections.length === 0 ? (
          <div className="empty-state">
            <i className="fa-solid fa-inbox"></i>
            <p>Aucune collection trouvée</p>
          </div>
        ) : (
          filteredCollections.map(collection => (
            <div key={collection._id} className="collection-card">
              <div className="collection-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <h2 style={{ margin: 0 }}>{collection.name}</h2>
                        <p style={{ margin: 0 }}>{collection.description}</p>
                      </div>
                      {viewMode === 'general' && (
                        <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleOpenAddStores(collection)}
                            title="Ajouter des magasins"
                          >
                            <i className="fa-solid fa-store"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteCollection(collection._id)}
                            title="Supprimer la collection"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => printCollectionTable(collection)}
                            title="Générer tableau"
                          >
                            <i className="fa-solid fa-print"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  <span className={`status-badge status-${collection.status}`}>
                    {collection.status}
                  </span>
                </div>
                <div className="collection-header-actions">
                  <button
                    className="btn-add-stores"
                    onClick={() => handleOpenAddStores(collection)}
                    title="Ajouter des magasins"
                  >
                    <i className="fa-solid fa-store"></i>
                    <i className="fa-solid fa-plus"></i>
                  </button>
                  {viewMode === 'general' && (
                    <button
                      className="btn btn-outline-primary ms-2"
                      onClick={() => printCollectionTable(collection)}
                      title="Générer tableau"
                    >
                      <i className="fa-solid fa-print"></i>
                    </button>
                  )}
                  <button
                    className="btn-delete"
                    onClick={() => handleDeleteCollection(collection._id)}
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>

              {viewMode === 'grouped' && (
                <div className="stores-grid">
                  {collection.stores?.map((store, idx) => {
                    // Filter out deleted products
                    const activeProducts = store.products?.filter(p => (p.status || '').toString().toLowerCase().trim() !== 'deleted' && getProductStoreCount(collection._id, p.product_id) > 0) || [];
                    const visibleProducts = activeProducts.slice(0, 6);
                    const hasMore = activeProducts.length > 6;

                    return (
                      <div key={idx} className="store-card">
                        <div className="store-card-header">
                          <h4>{getStoreName(store.store_id)}</h4>
                          <div className="store-actions">
                            <button
                              className="btn-manage-products"
                              onClick={() => handleOpenManageProducts(
                                collection._id, 
                                store.store_id, 
                                getStoreName(store.store_id),
                                store.products || []
                              )}
                              title="Gérer les produits"
                            >
                              <i className="fa-solid fa-edit"></i>
                            </button>
                            <button
                              className="btn-delete-store"
                              onClick={() => handleDeleteStoreFromCollection(collection._id, store.store_id)}
                              title="Retirer ce magasin"
                            >
                              <i className="fa-solid fa-times"></i>
                            </button>
                            <span className="product-count">
                              {activeProducts.length || 0} produits
                            </span>
                          </div>
                        </div>

                        <div className="products-grid-compact">
                          {visibleProducts.map((product, pIdx) => {
                            const productDetails = getProductDetails(product.product_id);
                            return (
                              <div key={pIdx} className="product-card-compact">
                                <div className="product-image-small">
                                  {getProductImage(product.product_id) ? (
                                    <img
                                      src={getProductImage(product.product_id)}
                                      alt={productDetails?.name || 'Product'}
                                    />
                                  ) : (
                                    <div className="product-no-image-small">
                                      <i className="fa-solid fa-image"></i>
                                    </div>
                                  )}
                                </div>
                                <div className="product-info-small">
                                  <h6 title={productDetails?.name}>
                                    {productDetails?.name || 'Unknown'}
                                  </h6>
                                  <div className="product-quantities">
                                    <span>Qté: {product.quantity}</span>
                                    {(product.quantity_real > 0 || product.quantity > 0) && (() => {
                                      const storeCount = getProductStoreCount(collection._id, product.product_id);
                                      const totalReal = getTotalQuantityReal(collection._id, product.product_id);
                                      return (
                                        <span className="quantity-real">
                                          Réel: {product.quantity_real || 0}
                                          {storeCount > 1 && (
                                            <span className="multi-store-badge" title={`Total dans ${storeCount} magasins`}>
                                              <i className="fa-solid fa-store" style={{ fontSize: '0.7em', marginLeft: '4px', marginRight: '2px' }}></i>
                                              {totalReal}
                                            </span>
                                          )}
                                        </span>
                                      );
                                    })()}
                                    <span className={`status-${product.status}`}>
                                      {product.status}
                                    </span>
                                  </div>
                                </div>
                                <div className="product-actions-compact">
                                  <button
                                    className="btn-edit-product-compact"
                                    onClick={() => handleOpenQuickEdit(collection._id, store.store_id, getStoreName(store.store_id), product)}
                                    title="Modifier"
                                  >
                                    <i className="fa-solid fa-pencil"></i>
                                  </button>
                                  <button
                                    className="btn-delete-product-compact"
                                    onClick={() => handleDeleteProduct(collection._id, store.store_id, product.product_id)}
                                    title="Retirer"
                                  >
                                    <i className="fa-solid fa-trash"></i>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {hasMore && (
                          <button
                            className="btn-show-more"
                            onClick={() => showMoreProducts(activeProducts, getStoreName(store.store_id))}
                          >
                            <i className="fa-solid fa-chevron-down"></i>
                            Voir plus ({activeProducts.length - 6} autres)
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {viewMode === 'filtered' && selectedStoreFilter && (
                <div className="filtered-products">
                  {collection.stores
                    ?.filter(store => normalizeId(store.store_id) === normalizeId(selectedStoreFilter.value))
                    .map((store, idx) => {
                      const activeProducts = store.products?.filter(p => (p.status || '').toString().toLowerCase().trim() !== 'deleted' && getProductStoreCount(collection._id, p.product_id) > 0) || [];
                      return (
                      <div key={idx}>
                        <div className="filtered-store-header">
                          <h4>{getStoreName(store.store_id)}</h4>
                          <div className="store-actions">
                            <button
                              className="btn-manage-products"
                              onClick={() => handleOpenManageProducts(
                                collection._id, 
                                store.store_id, 
                                getStoreName(store.store_id),
                                activeProducts
                              )}
                              title="Gérer les produits"
                            >
                              <i className="fa-solid fa-edit"></i>
                            </button>
                            <span className="product-count">
                              {activeProducts.length} produits
                            </span>
                          </div>
                        </div>
                        <div className="products-grid-filtered">
                          {activeProducts.map((product, pIdx) => {
                            const productDetails = getProductDetails(product.product_id);
                            return (
                              <div key={pIdx} className={`product-card-grid ${product.quantity > 0 ? 'has-quantity-warning' : ''}`}>
                                <div className="product-image-grid">
                                  {getProductImage(product.product_id) ? (
                                    <img
                                      src={getProductImage(product.product_id)}
                                      alt={productDetails?.name || 'Product'}
                                    />
                                  ) : (
                                    <div className="product-no-image">
                                      <i className="fa-solid fa-image"></i>
                                    </div>
                                  )}
                                </div>
                                <div className="product-info-grid">
                                  <h5 title={productDetails?.name}>{productDetails?.name || 'Unknown Product'}</h5>
                                  <div className="product-details-grid">
                                    <span className="quantity-badge">Qté: {product.quantity}</span>
                                    {product.quantity > 0 && (() => {
                                      const storeCount = getProductStoreCount(collection._id, product.product_id);
                                      const totalReal = getTotalQuantityReal(collection._id, product.product_id);
                                      return (
                                        <span className="quantity-real-badge">
                                          Réel: {product.quantity_real || 0}
                                          {storeCount > 1 && (
                                            <span className="multi-store-badge" title={`Total dans ${storeCount} magasins`}>
                                              <i className="fa-solid fa-store" style={{ fontSize: '0.7em', marginLeft: '4px', marginRight: '2px' }}></i>
                                              {totalReal}
                                            </span>
                                          )}
                                        </span>
                                      );
                                    })()}
                                    <span className={`status-badge status-${product.status}`}>
                                      {product.status}
                                    </span>
                                  </div>
                                </div>
                                <div className="product-actions-grid">
                                  <button
                                    className="btn-edit-product-compact"
                                    onClick={() => handleOpenQuickEdit(collection._id, store.store_id, getStoreName(store.store_id), product)}
                                    title="Modifier"
                                  >
                                    <i className="fa-solid fa-pencil"></i>
                                  </button>
                                  <button
                                    className="btn-delete-product-compact"
                                    onClick={() => handleDeleteProduct(collection._id, store.store_id, product.product_id)}
                                    title="Retirer"
                                  >
                                    <i className="fa-solid fa-trash"></i>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {viewMode === "general" && (
  <div className="container-fluid py-4">
    {(() => {
      const allGeneralProducts = getCollectionProductSummary(collection);
      
      // Filter by selected stores if any
      let filteredByStores = allGeneralProducts;
      if (generalStoreFilter && generalStoreFilter.length > 0) {
        const selectedStoreIds = generalStoreFilter.map(s => normalizeId(s.value));
        filteredByStores = allGeneralProducts.filter(product => 
          product.storeEntries.some(entry => selectedStoreIds.includes(normalizeId(entry.storeId)))
        ).map(product => {
          const filteredEntries = product.storeEntries.filter(entry => selectedStoreIds.includes(normalizeId(entry.storeId)));
          return {
            ...product,
            storeEntries: filteredEntries,
            sumQuantity: filteredEntries.reduce((sum, entry) => sum + entry.quantity, 0),
            sumQuantityReal: filteredEntries.reduce((sum, entry) => sum + entry.quantityReal, 0)
          };
        });
      }
      
      // Filter products by search term and ensure sorted by name
      const generalProducts = generalSearchTerm.trim() 
        ? filteredByStores.filter(product => 
            product.productDetails?.name?.toLowerCase().includes(generalSearchTerm.toLowerCase())
          ).sort((a, b) => {
            const nameA = (a.productDetails?.name || '').toLowerCase();
            const nameB = (b.productDetails?.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          })
        : filteredByStores;

      if (allGeneralProducts.length === 0) {
        return (
          <div className="text-center py-5">
            <div className="display-4 text-secondary mb-3" style={{ opacity: 0.5 }}>
              <i className="fa-solid fa-box-open" />
            </div>
            <h5 className="text-muted mb-0">Aucun produit dans cette collection</h5>
            <p className="text-muted small mt-2">Ajoutez des produits pour commencer</p>
          </div>
        );
      }

      const totalQty = generalProducts.reduce((sum, p) => sum + p.sumQuantity, 0);
      const totalQtyReal = generalProducts.reduce((sum, p) => sum + p.sumQuantityReal, 0);
      const totalAvailable = generalProducts.reduce((sum, p) => sum + p.storeEntries.reduce((s, e) => s + (e.availableStock || 0), 0), 0);
      const difference = totalQty - totalQtyReal;

      return (
        <>
          {/* SEARCH BAR */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fa-solid fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Rechercher un produit par nom..."
                  value={generalSearchTerm}
                  onChange={(e) => setGeneralSearchTerm(e.target.value)}
                  style={{ fontSize: '1rem', boxShadow: 'none' }}
                />
                {generalSearchTerm && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setGeneralSearchTerm('')}
                    title="Effacer la recherche"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                )}
                {/* Print table button for General view */}
                <button
                  className="btn btn-outline-primary ms-2"
                  type="button"
                  onClick={() => {
                    const productsForPrint = generalProducts;
                    const cols = ['Image', 'Produit', 'Disponible Total', 'Magasin', 'Quantité', 'Quantité Réelle', 'Statut'];
                    const css = `
                      body{font-family:Arial,Helvetica,sans-serif;padding:20px}
                      table{border-collapse:collapse;width:100%}
                      th,td{border:1px solid #ddd;padding:8px}
                      th{background:#f4f4f4}
                      img{max-width:60px;height:auto}
                    `;
                    let rowsHtml = '';
                    productsForPrint.forEach(p => {
                      const img = getProductImage(p.productId) || '';
                      const totalAvailable = p.storeEntries.reduce((s,e) => s + (e.availableStock || 0), 0);
                      rowsHtml += `<tr><td rowspan='${Math.max(1,p.storeEntries.length+1)}'>${img?`<img src="${img}"/>`:''}</td><td rowspan='${Math.max(1,p.storeEntries.length+1)}'>${(p.productDetails?.name||'')}</td><td rowspan='${Math.max(1,p.storeEntries.length+1)}'>${totalAvailable}</td></tr>`;
                      p.storeEntries.forEach(se => {
                        rowsHtml += `<tr><td>${se.storeName || se.storeId}</td><td>${se.quantity || 0}</td><td>${se.quantityReal || 0}</td><td>${se.status || 'N/A'}</td></tr>`;
                      });
                    });
                    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Tableau produits</title><style>${css}</style></head><body><h2>Collection: ${collection.name}</h2><table><thead><tr>${cols.map(c=>`<th>${c}</th>`).join('')}</tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`;
                    const w = window.open('', '_blank');
                    if (!w) { alert('Impossible d\'ouvrir la fenêtre d\'impression (popup bloquée).'); return; }
                    w.document.write(html);
                    w.document.close();
                    w.focus();
                    setTimeout(() => { w.print(); }, 250);
                  }}
                >
                  <i className="fa-solid fa-print"></i>&nbsp;Générer tableau
                </button>
              </div>
              {generalSearchTerm && (
                <div className="text-muted small mt-2">
                  <i className="fa-solid fa-info-circle me-1"></i>
                  {generalProducts.length} produit{generalProducts.length > 1 ? 's' : ''} trouvé{generalProducts.length > 1 ? 's' : ''} sur {allGeneralProducts.length}
                </div>
              )}
            </div>
          </div>

          {generalProducts.length === 0 ? (
            <div className="text-center py-5">
              <div className="display-4 text-secondary mb-3" style={{ opacity: 0.5 }}>
                <i className="fa-solid fa-search" />
              </div>
              <h5 className="text-muted mb-0">Aucun produit trouvé</h5>
              <p className="text-muted small mt-2">Essayez un autre terme de recherche</p>
              <button
                className="btn btn-outline-primary mt-3"
                onClick={() => setGeneralSearchTerm('')}
              >
                <i className="fa-solid fa-times me-2"></i>
                Effacer la recherche
              </button>
            </div>
          ) : (
          <>
          {/* ENHANCED STATS */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-xl-3">
              <div className="card border-0 shadow-sm h-100 hover-shadow" style={{ transition: 'all 0.2s' }}>
                <div className="card-body d-flex align-items-center gap-3">
                  <div className="rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 60, height: 60, backgroundColor: '#0d6efd' }}>
                    <i className="fa-solid fa-boxes-stacked fs-4" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <div className="fw-bold fs-3" style={{ color: '#000' }}>{generalProducts.length}</div>
                    <div className="text-muted small text-uppercase fw-semibold" style={{ letterSpacing: '0.5px' }}>Produits Uniques</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-3">
              <div className="card border-0 shadow-sm h-100 hover-shadow" style={{ transition: 'all 0.2s' }}>
                <div className="card-body d-flex align-items-center gap-3">
                  <div className="rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 60, height: 60, backgroundColor: '#ffc107' }}>
                    <i className="fa-solid fa-warehouse fs-4" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <div className="fw-bold fs-3" style={{ color: '#000' }}>{collection.stores?.length || 0}</div>
                    <div className="text-muted small text-uppercase fw-semibold" style={{ letterSpacing: '0.5px' }}>Magasins</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-3">
              <div className="card border-0 shadow-sm h-100 hover-shadow" style={{ transition: 'all 0.2s' }}>
                <div className="card-body d-flex align-items-center gap-3">
                  <div className="rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 60, height: 60, backgroundColor: '#0dcaf0' }}>
                    <i className="fa-solid fa-boxes-stacked fs-4" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <div className="fw-bold fs-3" style={{ color: '#000' }}>{totalAvailable}</div>
                    <div className="text-muted small text-uppercase fw-semibold" style={{ letterSpacing: '0.5px' }}>Disponible Total</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-3">
              <div className="card border-0 shadow-sm h-100 hover-shadow" style={{ transition: 'all 0.2s' }}>
                <div className="card-body d-flex align-items-center gap-3">
                  <div className="rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 60, height: 60, backgroundColor: '#198754' }}>
                    <i className="fa-solid fa-check-circle fs-4" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <div className="fw-bold fs-3 text-success">{totalQtyReal}</div>
                    <div className="text-muted small text-uppercase fw-semibold" style={{ letterSpacing: '0.5px' }}>Quantité Réelle</div>
                    {difference > 0 && (
                      <div className="badge mt-1 px-2 py-1" style={{ fontSize: '0.7rem', backgroundColor: '#dc3545', color: '#fff' }}>
                        <i className="fa-solid fa-exclamation-triangle me-1" />
                        Écart: {difference}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ENHANCED PRODUCTS GRID */}
          <div className="row g-4">
            {generalProducts.map((product) => (
              <div key={product.productId} className="col-12 col-md-6 col-xl-3">
                <div className="card border-0 shadow-sm h-100 hover-lift" style={{ transition: 'all 0.3s', overflow: 'hidden' }}>
                  {/* Product Header with Gradient */}
                  <div className="card-header border-0 bg-gradient" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <div className="d-flex gap-3 align-items-center">
                      {/* Enhanced Image */}
                      <div
                        className="border border-white border-3 rounded-3 d-flex align-items-center justify-content-center bg-white shadow"
                        style={{ width: 90, height: 90, overflow: "hidden", flexShrink: 0 }}
                      >
                        {getProductImage(product.productId) ? (
                          <img
                            src={getProductImage(product.productId)}
                            alt={product.productDetails?.name || "Product"}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                parent.innerHTML =
                                  '<div class="w-100 h-100 d-flex align-items-center justify-content-center text-muted"><i class="fa-solid fa-image fa-2x"></i></div>';
                              }
                            }}
                          />
                        ) : (
                          <div className="text-muted">
                            <i className="fa-solid fa-image fa-2x" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-grow-1 text-white" style={{ minWidth: 0 }}>
                        <h5 className="mb-2 fw-bold" style={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: '1.3',
                          color: '#fff'
                        }} title={product.productDetails?.name}>
                          {product.productDetails?.name || "Produit Inconnu"}
                        </h5>

                        {/* Summary Badges */}
                        <div className="d-flex flex-wrap gap-2">
                          <span className="badge fw-semibold px-3 py-2" style={{ fontSize: '0.85rem', backgroundColor: '#fff', color: '#0d6efd' }}>
                            <i className="fa-solid fa-cubes me-1" />
                            {product.sumQuantity}
                          </span>
                          <span className="badge fw-semibold px-3 py-2" style={{ fontSize: '0.85rem', backgroundColor: '#198754', color: '#fff' }}>
                            <i className="fa-solid fa-check-double me-1" />
                            {product.sumQuantityReal}
                          </span>
                          <span className="badge fw-semibold px-3 py-2" style={{ fontSize: '0.85rem', backgroundColor: '#0dcaf0', color: '#fff' }}>
                            <i className="fa-solid fa-boxes-stacked me-1" />
                            {product.storeEntries.reduce((sum, entry) => sum + (entry.availableStock || 0), 0)}
                          </span>
                          <span className="badge fw-normal px-3 py-2" style={{ fontSize: '0.75rem', backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff' }}>
                            <i className="fa-solid fa-store me-1" />
                            {product.storeEntries.length} magasin{product.storeEntries.length > 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Store Details */}
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h6 className="mb-0 fw-bold" style={{ color: '#000' }}>Détails par Magasin</h6>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleOpenAddStoreToProduct(
                          collection,
                          product.productId,
                          product.storeEntries.map(e => e.storeId)
                        )}
                        title="Ajouter un magasin"
                      >
                        <i className="fa-solid fa-store"></i>
                      </button>
                    </div>

                    <div className="d-flex flex-column gap-2" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {product.storeEntries.map((entry, idx) => (
                        <div
                          key={`${product.productId}-${entry.storeId}`}
                          className="border rounded-3 p-3 bg-light bg-opacity-50 hover-bg-white"
                          style={{ transition: 'all 0.2s' }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="d-flex align-items-center flex-grow-1 me-2" style={{ minWidth: 0 }}>
                              <div className="bg-white rounded-circle p-2 me-2 shadow-sm" style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="fa-solid fa-store text-primary" style={{ fontSize: '0.75rem' }} />
                              </div>
                              <span className="fw-semibold text-truncate" style={{ color: '#000' }} title={entry.storeName}>{entry.storeName}</span>
                            </div>
                            <span className="badge px-2 py-1" style={{ backgroundColor: '#0d6efd', color: '#fff', fontSize: '0.7rem' }}>#{idx + 1}</span>
                          </div>

                          <div className="d-flex gap-2">
                            <div className="flex-fill">
                              <div 
                                className="bg-white border rounded-2 text-center" 
                                style={{ cursor: 'pointer' }}
                                onDoubleClick={() => handleOpenEditQuantity(
                                  collection._id,
                                  entry.storeId,
                                  entry.storeName,
                                  product.productId,
                                  entry.quantity,
                                  entry.quantityReal
                                )}
                                title="Double-cliquer pour modifier"
                              >
                                <div className="small" style={{ fontSize: '0.65rem', color: '#6c757d', marginBottom: '2px' }}>Quantité</div>
                                <div className="fw-bold" style={{ color: '#000', fontSize: '1.1rem' }}>{entry.quantity}</div>
                              </div>
                            </div>
                            <div className="flex-fill">
                              <div 
                                className="border rounded-2 text-center" 
                                style={{ backgroundColor: '#d1e7dd', borderColor: '#198754', cursor: 'pointer' }}
                                onDoubleClick={() => handleOpenEditQuantity(
                                  collection._id,
                                  entry.storeId,
                                  entry.storeName,
                                  product.productId,
                                  entry.quantity,
                                  entry.quantityReal
                                )}
                                title="Double-cliquer pour modifier"
                              >
                                <div className="small" style={{ fontSize: '0.65rem', color: '#0f5132', marginBottom: '2px' }}>
                                  <i className="fa-solid fa-check me-1" />Réel
                                </div>
                                <div className="fw-bold" style={{ color: '#0f5132', fontSize: '1.1rem' }}>{entry.quantityReal}</div>
                              </div>
                            </div>
                            <div className="flex-fill">
                              <div className="border rounded-2 text-center" style={{ backgroundColor: '#cfe2ff', borderColor: '#0d6efd' }}>
                                <div className="small" style={{ fontSize: '0.65rem', color: '#084298', marginBottom: '2px' }}>
                                  <i className="fa-solid fa-boxes-stacked me-1" />Disponible
                                </div>
                                <div className="fw-bold" style={{ color: '#084298', fontSize: '1.1rem' }}>{entry.availableStock || 0}</div>
                              </div>
                            </div>
                            <button
                              className="btn btn-sm border-0 d-flex p-1 align-items-center justify-content-center"
                              style={{ 
                                width: '32px', 
                                height: '100%', 
                                backgroundColor: '#dc3545',
                                color: '#fff',
                                borderRadius: '6px',
                                padding: 0,
                                flexShrink: 0
                              }}
                              onClick={() => handleDeleteProduct(collection._id, entry.storeId, product.productId)}
                              title="Retirer ce produit du magasin"
                            >
                              <i className="fa-solid fa-trash" style={{ fontSize: '0.75rem' }} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </>
          )}
        </>
      );
    })()}
  </div>
)}

            </div>
          ))
        )}
      </div>

      {/* Add Collection Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouvelle Collection Spéciale</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleCreateCollection}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    required
                    value={newCollection.name}
                    onChange={(e) => setNewCollection({ ...newCollection, name: e.target.value })}
                    placeholder="Nom de la collection"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newCollection.description}
                    onChange={(e) => setNewCollection({ ...newCollection, description: e.target.value })}
                    placeholder="Description de la collection"
                    rows="4"
                  />
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={newCollection.status}
                    onChange={(e) => setNewCollection({ ...newCollection, status: e.target.value })}
                  >
                    <option value="draft">Brouillon</option>
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                    <option value="archived">Archivé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Magasins</label>
                  <Select
                    isMulti
                    options={storeOptions}
                    value={selectedStoresForNew}
                    onChange={setSelectedStoresForNew}
                    placeholder="Sélectionner des magasins..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Créer la Collection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Products Modal */}
      <ProductsModal
        show={showProductsModal}
        onClose={() => setShowProductsModal(false)}
        products={selectedStoreProducts.products}
        storeName={selectedStoreProducts.storeName}
        allProducts={allProducts}
      />

      {/* Manage Products Modal */}
      <ManageProductsModal
        show={showManageProductsModal}
        onClose={handleCloseManageProducts}
        collectionId={currentStoreForProducts.collectionId}
        storeId={currentStoreForProducts.storeId}
        storeName={currentStoreForProducts.storeName}
        allProducts={allProducts}
        existingProducts={currentStoreForProducts.existingProducts}
        onSave={handleProductsSaved}
      />

      {/* Add Stores to Collection Modal */}
      {showAddStoresModal && (
        <div className="modal-overlay" onClick={() => setShowAddStoresModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ajouter des magasins - {selectedCollectionForStores?.name}</h3>
              <button className="modal-close" onClick={() => setShowAddStoresModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Sélectionner des magasins</label>
                <Select
                  isMulti
                  options={storeOptions.filter(opt => 
                    !selectedCollectionForStores?.stores?.some(s => 
                      normalizeId(s.store_id) === normalizeId(opt.value)
                    )
                  )}
                  value={selectedStoresToAdd}
                  onChange={setSelectedStoresToAdd}
                  placeholder="Choisir des magasins..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowAddStoresModal(false)}>
                Annuler
              </button>
              <button type="button" className="btn-primary" onClick={handleAddStoresToCollection}>
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Edit Product Modal */}
      {showQuickEditModal && quickEditProduct && (
        <div className="modal-overlay" onClick={() => setShowQuickEditModal(false)}>
          <div className="modal-content modal-quick-edit" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Modifier le produit - {quickEditProduct.storeName}</h3>
              <button className="modal-close" onClick={() => setShowQuickEditModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Produit</label>
                <p className="product-name-display">
                  {getProductDetails(quickEditProduct.product_id)?.name || 'Unknown Product'}
                </p>
              </div>
              <div className="form-group">
                <label>Quantité</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={quickEditProduct.quantity}
                  onChange={(e) => setQuickEditProduct({ ...quickEditProduct, quantity: parseFloat(e.target.value) || 0 })}
                />
              </div>
              {quickEditProduct.quantity > 0 && (
                <div className="form-group">
                  <label>Quantité réelle</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={quickEditProduct.quantity_real}
                    onChange={(e) => setQuickEditProduct({ ...quickEditProduct, quantity_real: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Statut</label>
                <select
                  value={quickEditProduct.status}
                  onChange={(e) => setQuickEditProduct({ ...quickEditProduct, status: e.target.value })}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="deleted">Deleted</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowQuickEditModal(false)}>
                Annuler
              </button>
              <button type="button" className="btn-primary" onClick={handleQuickEditSave}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Store to Product Modal */}
      {showAddStoreToProductModal && addStoreToProduct && (
        <div className="modal-overlay" onClick={() => setShowAddStoreToProductModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ajouter un magasin au produit</h3>
              <button className="modal-close" onClick={() => setShowAddStoreToProductModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Produit</label>
                <p className="product-name-display">
                  {getProductDetails(addStoreToProduct.productId)?.name || 'Unknown Product'}
                </p>
              </div>
              <div className="form-group">
                <label>Magasin *</label>
                <Select
                  options={(() => {
                    // Get all stores in the collection
                    const collectionStoreIds = addStoreToProduct.collectionStores.map(s => normalizeId(s.store_id));
                    // Filter to show only stores in collection that don't already have this product
                    return storeOptions.filter(opt => {
                      const optId = normalizeId(opt.value);
                      return collectionStoreIds.includes(optId) && !addStoreToProduct.existingStores.includes(optId);
                    });
                  })()}
                  value={addStoreToProduct.selectedStore}
                  onChange={(selected) => setAddStoreToProduct({ ...addStoreToProduct, selectedStore: selected })}
                  placeholder="Sélectionner un magasin..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  noOptionsMessage={() => "Aucun magasin disponible"}
                />
              </div>
              <div className="form-group">
                <label>Quantité</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={addStoreToProduct.quantity}
                  onChange={(e) => setAddStoreToProduct({ ...addStoreToProduct, quantity: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>Quantité réelle</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={addStoreToProduct.quantityReal}
                  onChange={(e) => setAddStoreToProduct({ ...addStoreToProduct, quantityReal: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowAddStoreToProductModal(false)}>
                Annuler
              </button>
              <button type="button" className="btn-primary" onClick={handleSaveAddStoreToProduct}>
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Quantity Modal */}
      {showEditQuantityModal && editQuantityData && (
        <div className="modal-overlay" onClick={() => setShowEditQuantityModal(false)}>
          <div className="modal-content modal-quick-edit" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Modifier la quantité - {editQuantityData.storeName}</h3>
              <button className="modal-close" onClick={() => setShowEditQuantityModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Produit</label>
                <p className="product-name-display">
                  {getProductDetails(editQuantityData.productId)?.name || 'Unknown Product'}
                </p>
              </div>
              <div className="form-group">
                <label>Quantité</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={editQuantityData.quantity}
                  onChange={(e) => setEditQuantityData({ ...editQuantityData, quantity: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>Quantité réelle</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={editQuantityData.quantityReal}
                  onChange={(e) => setEditQuantityData({ ...editQuantityData, quantityReal: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowEditQuantityModal(false)}>
                Annuler
              </button>
              <button type="button" className="btn-primary" onClick={handleSaveEditQuantity}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialCollections;
